"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useAnchored, useDismiss, useEscapeLayer, useFocusTrap, useMounted, type Placement } from "./hooks.ts";
import { ChevronIcon } from "./icons.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   The one floating layer.

   Every menu, combobox, colour picker and date picker in the admin is this
   component with different children. Doing it once is what makes the
   behaviour consistent: outside click, Escape, focus return, collision
   flipping and viewport clamping are written here and nowhere else, so a
   control added later cannot get any of them subtly wrong.

   Rendered through a portal into `document.body` and positioned `fixed`. An
   absolutely-positioned popover inherits its parent's clipping, and half the
   panels in this product have `overflow: hidden`.
   ────────────────────────────────────────────────────────────────────────── */

export function Popover({
  anchorRef,
  open,
  onClose,
  children,
  placement = "bottom-start",
  matchWidth = false,
  minWidth,
  className = "",
  role = "dialog",
  label,
  trapFocus = true,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  placement?: Placement;
  matchWidth?: boolean;
  minWidth?: number;
  className?: string;
  role?: "dialog" | "listbox" | "menu" | "none";
  label?: string;
  trapFocus?: boolean;
}) {
  const floating = useRef<HTMLDivElement>(null);
  const mounted = useMounted();
  const style = useAnchored(anchorRef, floating, open, { placement, matchWidth, minWidth });

  useDismiss([anchorRef, floating], open, onClose);
  // The trap holds focus, but does not place it: the popover renders once with
  // `visibility: hidden` while it is measured, and focusing a hidden element
  // does nothing at all. So placement waits for the measurement.
  useFocusTrap(floating, open && trapFocus, false);

  const positioned = Boolean(style);
  useEffect(() => {
    if (!open || !positioned || !trapFocus) return;
    const node = floating.current;
    if (!node) return;
    const target =
      node.querySelector<HTMLElement>("[data-autofocus]") ??
      node.querySelector<HTMLElement>('input, button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    (target ?? node).focus({ preventScroll: true });
  }, [open, positioned, trapFocus]);

  if (!mounted || !open) return null;

  return createPortal(
    <div
      ref={floating}
      className={`pop ${style?.side ?? "down"} ${className}`.trim()}
      role={role === "none" ? undefined : role}
      aria-label={label}
      style={{
        left: style?.left ?? -9999,
        top: style?.top ?? -9999,
        minWidth: style?.minWidth,
        maxHeight: style?.maxHeight,
        // Hidden until measured, so it never paints in the top-left corner
        // for one frame before jumping to the anchor.
        visibility: style ? "visible" : "hidden",
      }}
    >
      {children}
    </div>,
    document.body
  );
}

/* ── the trigger + popover pair, for the common case ───────────────────── */

export function usePopover() {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((value) => !value), []);
  return { anchorRef, open, setOpen, close, toggle };
}

/* ── context menu ──────────────────────────────────────────────────────── */

export type MenuItem =
  | {
      kind: "item"; label: string; icon?: ReactNode; onSelect: () => void; danger?: boolean; disabled?: boolean; hint?: string;
      /** A second line under the label, such as why the item can't be picked right now. */
      note?: string;
      /** Opens a second menu to the right on hover, or with the right arrow, instead of acting on its own. onSelect is not called. */
      submenu?: MenuItem[];
    }
  | { kind: "divider" }
  | { kind: "section"; label: string };

/**
 * A right-click menu that also opens from a keyboard.
 *
 * It positions against a synthetic 1×1 anchor at the pointer, so it reuses the
 * same collision handling as everything else rather than having its own.
 */
export function ContextMenu({
  items,
  children,
  className = "",
}: {
  items: MenuItem[];
  children: ReactNode;
  className?: string;
}) {
  const [point, setPoint] = useState<{ x: number; y: number } | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const virtual = useRef<HTMLSpanElement>(null);

  const onContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setPoint({ x: event.clientX, y: event.clientY });
  };

  return (
    <span ref={anchorRef} className={className} onContextMenu={onContextMenu} style={{ display: "contents" }}>
      {children}
      {point ? (
        <>
          {/* In the body, not here: inside an element with content-visibility, contain or a transform,
              position: fixed is measured from that element, and the menu opened far from the pointer
              (the username menu in Research's niche check, 2026-11-05). */}
          {createPortal(
            <span
              ref={virtual}
              aria-hidden="true"
              style={{ position: "fixed", left: point.x, top: point.y, width: 1, height: 1, pointerEvents: "none" }}
            />,
            document.body,
          )}
          <Popover anchorRef={virtual} open onClose={() => setPoint(null)} role="menu" label="Actions" minWidth={180}>
            <MenuList items={items} onDone={() => setPoint(null)} />
          </Popover>
        </>
      ) : null}
    </span>
  );
}

export function MenuList({ items, onDone, autoFocus = true }: { items: MenuItem[]; onDone: () => void; autoFocus?: boolean }) {
  const listRef = useRef<HTMLDivElement>(null);
  const actionable = items.filter((item) => item.kind === "item");
  const [cursor, setCursor] = useState(0);
  // The row whose submenu is showing, and whether the keyboard opened it, which moves focus into it.
  const [sub, setSub] = useState<{ index: number; focus: boolean } | null>(null);

  useEffect(() => {
    if (!autoFocus) return;
    const first = listRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)");
    first?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyDown = (event: React.KeyboardEvent) => {
    const buttons = [...(listRef.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? [])];
    if (buttons.length === 0) return;
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      buttons[(index + 1) % buttons.length].focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      buttons[(index - 1 + buttons.length) % buttons.length].focus();
    }
    setCursor(Math.max(0, index));
  };

  return (
    <div className="pop-options" ref={listRef} onKeyDown={onKeyDown} role="none">
      {items.map((item, index) => {
        if (item.kind === "divider") return <span key={`d${index}`} className="pop-divider" />;
        if (item.kind === "section")
          return (
            <span key={`s${index}`} className="pop-section">
              {item.label}
            </span>
          );
        return (
          <MenuRow
            key={`${item.label}-${index}`}
            item={item}
            sub={sub?.index === index ? sub : null}
            setSub={(next) => setSub(next === null ? null : { index, focus: next })}
            onDone={onDone}
          />
        );
      })}
      {actionable.length === 0 ? <div className="panel-empty">Nothing to do here.</div> : null}
    </div>
  );
}

/** One row of a menu. A row with a submenu opens it on hover, click, Enter or the right arrow, and never acts itself. */
function MenuRow({
  item,
  sub,
  setSub,
  onDone,
}: {
  item: Extract<MenuItem, { kind: "item" }>;
  sub: { focus: boolean } | null;
  /** true opens this row's submenu with focus in it, false opens it without, null closes whichever is open. */
  setSub: (focus: boolean | null) => void;
  onDone: () => void;
}) {
  const row = useRef<HTMLButtonElement>(null);
  const nested = item.submenu && !item.disabled ? item.submenu : null;
  const open = nested !== null && sub !== null;
  return (
    <>
      <button
        ref={row}
        type="button"
        role="menuitem"
        disabled={item.disabled}
        className={open ? "cursor" : undefined}
        aria-haspopup={item.submenu ? "menu" : undefined}
        aria-expanded={item.submenu ? open : undefined}
        style={item.danger ? { color: "var(--red)" } : undefined}
        onMouseEnter={() => (nested ? !open && setSub(false) : setSub(null))}
        onKeyDown={(event) => {
          if (nested && (event.key === "ArrowRight" || event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            setSub(true);
          }
        }}
        onClick={() => {
          if (nested) return setSub(true);
          item.onSelect();
          onDone();
        }}
      >
        <span style={{ display: "grid", placeItems: "center", width: 15 }}>{item.icon}</span>
        <span className="pop-option-copy">
          <strong>
            <span>{item.label}</span>
          </strong>
          {item.note ? <small>{item.note}</small> : null}
        </span>
        {item.submenu ? <ChevronIcon direction="right" size={13} /> : item.hint ? <kbd className="key">{item.hint}</kbd> : <span />}
      </button>
      {open ? (
        <Submenu
          row={row}
          items={nested}
          focus={sub.focus}
          onDone={onDone}
          onClose={() => {
            setSub(null);
            row.current?.focus();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * A second menu beside its row: to the right of the menu, or to the left when
 * the screen's edge is too close, and always inside the viewport.
 *
 * It is portalled like every popover, so it is outside the parent menu in the
 * DOM. The pop-sub class is how the parent's outside-click check (useDismiss)
 * knows a click in here is still inside the menu.
 */
function Submenu({
  row,
  items,
  focus,
  onDone,
  onClose,
}: {
  row: RefObject<HTMLButtonElement | null>;
  items: MenuItem[];
  focus: boolean;
  onDone: () => void;
  onClose: () => void;
}) {
  const floating = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);
  useEscapeLayer(true, onClose);

  useEffect(() => {
    const anchor = row.current;
    const node = floating.current;
    if (!anchor || !node) return;
    const menu = anchor.closest(".pop")?.getBoundingClientRect() ?? anchor.getBoundingClientRect();
    const own = anchor.getBoundingClientRect();
    const margin = 8;
    const right = menu.right + 4;
    const left = right + node.offsetWidth <= window.innerWidth - margin ? right : Math.max(margin, menu.left - 4 - node.offsetWidth);
    const top = Math.max(margin, Math.min(own.top - 6, window.innerHeight - node.offsetHeight - margin));
    setPlace({ left: Math.round(left), top: Math.round(top) });
  }, [row]);

  // Focus waits for the measurement, as in Popover: a hidden element cannot take it.
  // Opened by hover, focus stays where it was.
  useEffect(() => {
    if (place && focus) floating.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
  }, [place, focus]);

  return createPortal(
    <div
      ref={floating}
      className="pop pop-sub down"
      role="menu"
      style={{ left: place?.left ?? -9999, top: place?.top ?? -9999, minWidth: 170, visibility: place ? "visible" : "hidden" }}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onKeyDown={(event) => {
        // React carries key events through the portal to the parent menu, which would move its own focus.
        event.stopPropagation();
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          onClose();
        }
      }}
    >
      <MenuList items={items} onDone={onDone} autoFocus={false} />
    </div>,
    document.body
  );
}

/** The trigger-plus-menu pattern, which appears on nearly every table row. */
export function MenuButton({
  items,
  label = "Actions",
  className = "icon-button ghost small",
  children,
  placement = "bottom-end",
}: {
  items: MenuItem[];
  label?: string;
  className?: string;
  children: ReactNode;
  placement?: Placement;
}) {
  const { anchorRef, open, setOpen, close, toggle } = usePopover();
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={className}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={(event) => {
          event.stopPropagation();
          toggle();
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {children}
      </button>
      <Popover anchorRef={anchorRef} open={open} onClose={close} role="menu" label={label} placement={placement} minWidth={190}>
        <MenuList items={items} onDone={close} />
      </Popover>
    </>
  );
}
